import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_daycare-visit-book/artifacts/hfd2nsp4_Screenshot%202026-03-17%20at%209.00.03%E2%80%AFPM.png";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/programs", label: "Programs" },
    { path: "/gallery", label: "Gallery" },
    { path: "/book-visit", label: "Book a Visit" },
  ];

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <nav className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-6xl transition-all duration-500 ${scrolled ? 'top-2' : 'top-4'}`}>
      <div className={`glass-nav rounded-full border border-orange-100 shadow-lg shadow-orange-500/5 px-6 py-3 transition-all duration-300 ${scrolled ? 'shadow-xl py-2' : ''}`}>
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group"
            data-testid="navbar-logo"
          >
            <img 
              src={LOGO_URL} 
              alt="Good Hands Family Daycare" 
              className="h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link, index) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 hover:scale-105 ${
                  isActive(link.path)
                    ? "bg-yellow-400 text-slate-900 shadow-md"
                    : "text-slate-700 hover:bg-yellow-100"
                }`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Buttons (Desktop) */}
          <div className="hidden md:flex items-center gap-3">
            <a 
              href="tel:+15715359999" 
              className="flex items-center gap-2 text-slate-600 hover:text-yellow-600 transition-colors group"
            >
              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center group-hover:bg-yellow-200 group-hover:scale-110 transition-all">
                <Phone className="w-4 h-4 text-yellow-600" />
              </div>
            </a>
            <Link to="/book-visit">
              <Button 
                className="btn-playful text-sm px-6 py-2 group"
                data-testid="navbar-book-tour-btn"
              >
                Book a Tour
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-full hover:bg-yellow-100 transition-all duration-300 hover:scale-110"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            <div className="relative w-6 h-6">
              <span className={`absolute top-1 left-0 w-6 h-0.5 bg-slate-800 transition-all duration-300 ${isOpen ? 'rotate-45 top-3' : ''}`} />
              <span className={`absolute top-3 left-0 w-6 h-0.5 bg-slate-800 transition-all duration-300 ${isOpen ? 'opacity-0' : ''}`} />
              <span className={`absolute top-5 left-0 w-6 h-0.5 bg-slate-800 transition-all duration-300 ${isOpen ? '-rotate-45 top-3' : ''}`} />
            </div>
          </button>
        </div>

        {/* Mobile Navigation */}
        <div className={`md:hidden overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? 'max-h-96 opacity-100 mt-4 pb-4' : 'max-h-0 opacity-0'}`}>
          <div className="flex flex-col gap-2">
            {navLinks.map((link, index) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 py-3 rounded-2xl text-sm font-medium transition-all duration-300 ${
                  isActive(link.path)
                    ? "bg-yellow-400 text-slate-900"
                    : "text-slate-700 hover:bg-yellow-100"
                }`}
                style={{ 
                  transform: isOpen ? 'translateX(0)' : 'translateX(-20px)',
                  opacity: isOpen ? 1 : 0,
                  transitionDelay: `${index * 50}ms`
                }}
              >
                {link.label}
              </Link>
            ))}
            <div className="flex gap-3 mt-2" style={{ 
              transform: isOpen ? 'translateX(0)' : 'translateX(-20px)',
              opacity: isOpen ? 1 : 0,
              transitionDelay: '200ms'
            }}>
              <a 
                href="tel:+15715359999" 
                className="flex-1"
              >
                <Button 
                  variant="outline"
                  className="w-full rounded-full font-bold border-2 border-yellow-400"
                >
                  <Phone className="mr-2 w-4 h-4" />
                  Call Us
                </Button>
              </a>
              <Link 
                to="/book-visit" 
                onClick={() => setIsOpen(false)}
                className="flex-1"
              >
                <Button 
                  className="btn-playful w-full text-sm"
                  data-testid="mobile-book-tour-btn"
                >
                  Book a Tour
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};
